/**
 * ImportWizard.js
 *
 * A 3-step MUI Stepper Dialog for importing CSV / OFX / QIF transaction files.
 *
 * Step 1 — Upload      : file picker / drag-and-drop zone
 * Step 2 — Preview     : dry-run results table (first 20 rows + duplicate banner)
 * Step 3 — Confirm     : summary + "Import" button; success / error feedback
 *
 * Props:
 *   open       {boolean}           controls dialog visibility
 *   onClose    {function}          called when the dialog should close
 *   onSuccess  {function(count)}   called after a successful import (optional)
 *   accountId  {string}            pre-fill account for all imported rows (optional)
 */

import React, { useState, useCallback, useRef } from 'react';
import axios from 'axios';

import Dialog           from '@mui/material/Dialog';
import DialogTitle      from '@mui/material/DialogTitle';
import DialogContent    from '@mui/material/DialogContent';
import DialogActions    from '@mui/material/DialogActions';
import Stepper          from '@mui/material/Stepper';
import Step             from '@mui/material/Step';
import StepLabel        from '@mui/material/StepLabel';
import Button           from '@mui/material/Button';
import Typography       from '@mui/material/Typography';
import Box              from '@mui/material/Box';
import Alert            from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Table            from '@mui/material/Table';
import TableBody        from '@mui/material/TableBody';
import TableCell        from '@mui/material/TableCell';
import TableContainer   from '@mui/material/TableContainer';
import TableHead        from '@mui/material/TableHead';
import TableRow         from '@mui/material/TableRow';
import Paper            from '@mui/material/Paper';
import Chip             from '@mui/material/Chip';
import IconButton       from '@mui/material/IconButton';
import Tooltip          from '@mui/material/Tooltip';
import useMediaQuery    from '@mui/material/useMediaQuery';
import { useTheme }     from '@mui/material/styles';

import UploadFileIcon   from '@mui/icons-material/UploadFile';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import CloseIcon        from '@mui/icons-material/Close';

import { API_CONFIG } from '../../config';

const API_BASE = API_CONFIG.API_BASE_URL
  ? API_CONFIG.API_BASE_URL.replace(/\/api$/, '')
  : (process.env.REACT_APP_API_URL || 'https://soldikeeper-backend-production.up.railway.app');
const STEP_LABELS = ['Upload File', 'Preview', 'Confirm'];

const ACCEPTED_EXTENSIONS = ['.csv', '.ofx', '.qfx', '.qif'];
const ACCEPTED_MIME = [
  'text/csv',
  'text/plain',
  'application/octet-stream',
  'application/x-ofx',
  'application/x-qif',
];

// ─── sub-components ──────────────────────────────────────────────────────────

function DropZone({ file, onFile, error }) {
  const inputRef  = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback(e => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) onFile(dropped);
  }, [onFile]);

  const handleChange = useCallback(e => {
    const selected = e.target.files[0];
    if (selected) onFile(selected);
  }, [onFile]);

  return (
    <Box
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current && inputRef.current.click()}
      sx={{
        border: '2px dashed',
        borderColor: dragOver ? 'primary.main' : error ? 'error.main' : 'divider',
        borderRadius: 2,
        p: 4,
        textAlign: 'center',
        cursor: 'pointer',
        bgcolor: dragOver ? 'action.hover' : 'background.paper',
        transition: 'all 0.2s',
        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS.join(',')}
        style={{ display: 'none' }}
        onChange={handleChange}
      />
      <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
      {file ? (
        <>
          <Typography variant="subtitle1" fontWeight={600}>{file.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {(file.size / 1024).toFixed(1)} KB
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="subtitle1">Drag & drop your file here</Typography>
          <Typography variant="body2" color="text.secondary">
            or click to browse — CSV, OFX, QFX, QIF (max 10 MB)
          </Typography>
        </>
      )}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </Box>
  );
}

function PreviewTable({ rows }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Category hint</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} hover>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {row.date ? new Date(row.date).toLocaleDateString() : '—'}
              </TableCell>
              <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.description || '—'}
              </TableCell>
              <TableCell align="right">
                {typeof row.amount === 'number' ? row.amount.toFixed(2) : '—'}
              </TableCell>
              <TableCell>
                <Chip
                  label={row.type || 'expense'}
                  size="small"
                  color={row.type === 'income' ? 'success' : 'default'}
                />
              </TableCell>
              <TableCell>{row.categoryHint || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

export default function ImportWizard({ open, onClose, onSuccess, accountId }) {
  const theme     = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeStep, setActiveStep]   = useState(0);
  const [file,       setFile]         = useState(null);
  const [fileError,  setFileError]    = useState('');
  const [loading,    setLoading]      = useState(false);
  const [apiError,   setApiError]     = useState('');

  // Step 2 state
  const [preview,    setPreview]      = useState([]);
  const [newCount,   setNewCount]     = useState(0);
  const [dupCount,   setDupCount]     = useState(0);

  // Step 3 state
  const [imported,   setImported]     = useState(null); // null = not done yet

  // ── handlers ───────────────────────────────────────────────────────────────

  const handleFileChange = useCallback(selected => {
    setFileError('');
    const ext = selected.name.split('.').pop().toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(`.${ext}`)) {
      setFileError(`Unsupported file type ".${ext}". Please upload CSV, OFX, QFX, or QIF.`);
      return;
    }
    if (selected.size > 10 * 1024 * 1024) {
      setFileError('File exceeds the 10 MB limit.');
      return;
    }
    setFile(selected);
  }, []);

  const handleAnalyse = useCallback(async () => {
    if (!file) { setFileError('Please select a file first.'); return; }
    setLoading(true);
    setApiError('');
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      if (accountId) formData.append('accountId', accountId);
      formData.append('dryRun', 'true');

      const { data } = await axios.post(
        `${API_BASE}/api/import/transactions`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setPreview(data.preview  || []);
      setNewCount(data.newCount || 0);
      setDupCount(data.duplicateCount || 0);
      setActiveStep(1);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Analysis failed.';
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }, [file, accountId]);

  const handleImport = useCallback(async () => {
    setLoading(true);
    setApiError('');
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      if (accountId) formData.append('accountId', accountId);
      formData.append('dryRun', 'false');

      const { data } = await axios.post(
        `${API_BASE}/api/import/transactions`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setImported(data.imported || 0);
      setActiveStep(2);
      if (onSuccess) onSuccess(data.imported || 0);
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Import failed.';
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  }, [file, accountId, onSuccess]);

  const handleClose = useCallback(() => {
    // Reset state
    setActiveStep(0);
    setFile(null);
    setFileError('');
    setLoading(false);
    setApiError('');
    setPreview([]);
    setNewCount(0);
    setDupCount(0);
    setImported(null);
    onClose();
  }, [onClose]);

  // ── step content ───────────────────────────────────────────────────────────

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <DropZone file={file} onFile={handleFileChange} error={fileError} />
            {apiError && <Alert severity="error" sx={{ mt: 2 }}>{apiError}</Alert>}
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Alert
              severity={dupCount > 0 ? 'warning' : 'success'}
              sx={{ mb: 2 }}
            >
              <strong>{newCount}</strong> new transaction{newCount !== 1 ? 's' : ''} found.
              {dupCount > 0 && <> &nbsp;<strong>{dupCount}</strong> duplicate{dupCount !== 1 ? 's' : ''} will be skipped.</>}
              {preview.length < newCount && ` (Showing first ${preview.length} rows)`}
            </Alert>
            {preview.length > 0 ? (
              <PreviewTable rows={preview} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No new transactions to import.
              </Typography>
            )}
            {apiError && <Alert severity="error" sx={{ mt: 2 }}>{apiError}</Alert>}
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            {imported !== null ? (
              <>
                <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  ✅ {imported} transaction{imported !== 1 ? 's' : ''} imported successfully!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {dupCount > 0 && `${dupCount} duplicate${dupCount !== 1 ? 's were' : ' was'} skipped.`}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  Ready to import <strong>{newCount}</strong> new transaction{newCount !== 1 ? 's' : ''}.
                </Typography>
                {dupCount > 0 && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {dupCount} duplicate{dupCount !== 1 ? 's' : ''} will be skipped.
                  </Typography>
                )}
                {accountId && (
                  <Typography variant="body2" color="text.secondary">
                    Account: {accountId}
                  </Typography>
                )}
                {apiError && <Alert severity="error" sx={{ mt: 2 }}>{apiError}</Alert>}
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  // ── actions ────────────────────────────────────────────────────────────────

  const renderActions = () => {
    if (activeStep === 0) {
      return (
        <>
          <Button onClick={handleClose} disabled={loading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAnalyse}
            disabled={loading || !file}
            startIcon={loading && <CircularProgress size={14} />}
          >
            {loading ? 'Analysing…' : 'Analyse'}
          </Button>
        </>
      );
    }

    if (activeStep === 1) {
      return (
        <>
          <Button onClick={() => { setActiveStep(0); setApiError(''); }} disabled={loading}>
            Back
          </Button>
          <Button
            variant="contained"
            onClick={() => setActiveStep(2)}
            disabled={loading || newCount === 0}
          >
            Continue
          </Button>
        </>
      );
    }

    if (activeStep === 2 && imported === null) {
      return (
        <>
          <Button onClick={() => setActiveStep(1)} disabled={loading}>Back</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={loading || newCount === 0}
            startIcon={loading && <CircularProgress size={14} />}
          >
            {loading ? 'Importing…' : `Import ${newCount} Transaction${newCount !== 1 ? 's' : ''}`}
          </Button>
        </>
      );
    }

    // Step 2 after success
    return (
      <Button variant="contained" onClick={handleClose}>Close</Button>
    );
  };

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: fullScreen ? '100%' : 520 } }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Typography variant="h6" component="span">Import Transactions</Typography>
        <Tooltip title="Close">
          <IconButton onClick={handleClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
          {STEP_LABELS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStep()}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {renderActions()}
      </DialogActions>
    </Dialog>
  );
}
